import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { BrandMark } from '@/components/BrandMark';
import { brandIdentity, withAlpha } from '@/lib/brands';

export interface RevealedMake {
  name: string;
  slug?: string;
}

interface BrandRevealContextValue {
  /** Fades the marque's logo up in the middle of the screen. Safe to call from anywhere. */
  reveal: (make: RevealedMake) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const BrandRevealContext = createContext<BrandRevealContextValue | null>(null);

/** Must match the `kk-reveal-*` keyframes in index.css, which own the actual choreography. */
const REVEAL_DURATION_MS = 2400;

interface RevealState extends RevealedMake {
  /** Bumped on every call so React remounts the overlay and the animation restarts from zero. */
  token: number;
}

/**
 * The moment a marque is chosen.
 *
 * Picking a make is the single most consequential thing a buyer does on this site, and until now it
 * felt like ticking a checkbox. The marque's own mark settles in the middle of the screen, holds,
 * then drifts out — translucent throughout, so it reads as something passing over the page rather
 * than a panel on top of it. An earlier version washed the entire viewport in the marque's colour
 * and blurred everything behind it; that read as a modal nobody asked for, so all that survives of
 * it is a soft halo behind the mark.
 *
 * The overlay never blocks input (`pointer-events: none`) and is shown without animation under
 * `prefers-reduced-motion`.
 */
export function BrandRevealProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<RevealState | null>(null);
  const counter = useRef(0);

  const reveal = useCallback((make: RevealedMake) => {
    counter.current += 1;
    setState({ ...make, token: counter.current });
  }, []);

  // Keyed on the whole state object, so someone who ticks four marques in quick succession sees the
  // last one rather than four overlapping washes: each new reveal cancels the previous timer.
  useEffect(() => {
    if (!state) {
      return;
    }

    const timer = window.setTimeout(() => setState(null), REVEAL_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [state]);

  const value = useMemo(() => ({ reveal }), [reveal]);

  return (
    <BrandRevealContext.Provider value={value}>
      {children}
      {state && <BrandRevealOverlay key={state.token} make={state} />}
    </BrandRevealContext.Provider>
  );
}

function BrandRevealOverlay({ make }: { make: RevealedMake }) {
  const identity = brandIdentity(make);

  return (
    <div className="brand-reveal" aria-hidden>
      <div className="brand-reveal__badge">
        {/* Halo sized off the mark, not the viewport — see `.brand-reveal__glow`. */}
        <span
          className="brand-reveal__glow"
          style={{ background: withAlpha(identity.colour, 0.45) }}
        />

        {identity.logo ? (
          <img src={identity.logo} alt="" className="brand-reveal__logo" decoding="async" />
        ) : (
          <BrandMark make={make} size="2xl" priority className="relative" />
        )}
      </div>
    </div>
  );
}
